<template>
  <v-container class="rm-drawer-panel">
    <RightDrawerHeader :title="headerTitle" showBack @back="handleBack" />
    <div class="rm-drawer-body">
      <EstimateRepairerCreation
        :currentRepairFile="currentRepairFile"
        v-if="estimateInCreation"
        ref="creationStep"
        @cancelEstimateRepairer="handleCancelEstimateRepairer()"
        @nextStepEstimateInOffer="nextStepEstimateInOffer()"
        @updateFooterState="updateFooterState"
      />
      <EstimateRepairerOffer
        @cancelEstimateRepairer="handleCancelEstimateRepairer()"
        @updateCurrentRepairFile="handleUpdateCurrentRepairFile()"
        @openEstimateRepairerDetail="handleOpenEstimateRepairerDetail()"
        :currentRepairFile="currentRepairFile"
        v-if="estimateInOffer"
        ref="offerStep"
        @updateFooterState="updateFooterState"
      />
      <EstimateRepairerDetail
        @cancelEstimateRepairer="handleCancelEstimateRepairer()"
        @closeNewAction="handleCloseNewAction()"
        @updateCurrentRepairFile="handleUpdateCurrentRepairFile()"
        @openEstimateRepairerClientChoice="handleOpenEstimateRepairerClientChoice()"
        @backToRepairerOffersCreation="handleBackToRepairerOffersCreation()"
        :currentRepairFile="currentRepairFile"
        v-if="estimateInDetail"
        ref="detailStep"
        @updateFooterState="updateFooterState"
      />
      <EstimateRepairerClientChoice
        @cancelEstimateRepairer="handleCancelEstimateRepairer()"
        @openEstimateRepairerClientChoiceValidation="
          handleOpenEstimateRepairerClientChoiceValidation()
        "
        @closeNewAction="handleCloseNewAction()"
        @updateCurrentRepairFile="handleUpdateCurrentRepairFile()"
        :currentRepairFile="currentRepairFile"
        v-if="estimateClientChoice"
        ref="clientChoiceStep"
        @updateFooterState="updateFooterState"
      />
      <EstimateRepairerClientChoiceValidation
        @cancelEstimateRepairer="handleCancelEstimateRepairer()"
        @updateCurrentRepairFile="handleUpdateCurrentRepairFile()"
        :currentRepairFile="currentRepairFile"
        v-if="estimateClientChoiceValidation"
        ref="clientChoiceValidationStep"
        @updateFooterState="updateFooterState"
      />
      <v-footer class="rm-drawer-footer justify-end ga-2">
        <v-btn v-if="estimateInDetail" variant="tonal" @click="handleCloseNewAction">
          {{ $t('ESTIMATE_REPAIRER_DETAIL.BUTTON.CANCEL') }}
        </v-btn>
        <v-btn
          :disabled="footerState.disabled"
          :loading="footerState.loading"
          :prepend-icon="activeFooterPrependIcon"
          :append-icon="activeFooterAppendIcon"
          @click="submitActiveStep"
        >
          {{ activeFooterLabel }}
        </v-btn>
      </v-footer>
    </div>
  </v-container>
</template>

<script>
import EstimateRepairerCreation from '@/components/repair-files/right-drawer/estimate-repairer/content/EstimateRepairerCreation.vue'
import EstimateRepairerOffer from '@/components/repair-files/right-drawer/estimate-repairer/content/EstimateRepairerOffer.vue'
import EstimateRepairerDetail from '@/components/repair-files/right-drawer/estimate-repairer/content/EstimateRepairerDetail.vue'
import RepairFileStatusDetailConstant from '@/constants/repair-module/repair-file/repairFileStatusDetailConstant'
import EstimateRepairerClientChoice from '@/components/repair-files/right-drawer/estimate-repairer/content/EstimateRepairerClientChoice.vue'
import EstimateRepairerClientChoiceValidation from '@/components/repair-files/right-drawer/estimate-repairer/content/EstimateRepairerClientChoiceValidation.vue'
import RightDrawerHeader from '@/components/ui/RightDrawerHeader.vue'

export default {
  components: {
    EstimateRepairerClientChoice,
    EstimateRepairerCreation,
    EstimateRepairerOffer,
    EstimateRepairerDetail,
    EstimateRepairerClientChoiceValidation,
    RightDrawerHeader
  },
  props: {
    currentRepairFile: Object
  },
  data() {
    return {
      loading: false,
      repairerRepairFileNumber: null,
      estimateInCreation: true,
      estimateInOffer: false,
      estimateInDetail: false,
      estimateClientChoice: false,
      estimateClientChoiceValidation: false,
      skipNextStatusSync: false,
      footerState: {
        disabled: false,
        loading: false
      }
    }
  },
  created() {
    this.syncStepFromStatus(this.currentRepairFile?.status_detail)
  },
  watch: {
    'currentRepairFile.status_detail'(newStatusDetail) {
      if (this.skipNextStatusSync) {
        this.skipNextStatusSync = false
        return
      }
      this.syncStepFromStatus(newStatusDetail)
    }
  },
  computed: {
    activeStepRef() {
      if (this.estimateInCreation) return 'creationStep'
      if (this.estimateInOffer) return 'offerStep'
      if (this.estimateInDetail) return 'detailStep'
      if (this.estimateClientChoice) return 'clientChoiceStep'
      if (this.estimateClientChoiceValidation) return 'clientChoiceValidationStep'
      return null
    },
    activeFooterLabel() {
      if (this.estimateInOffer) return this.$t('COMMON.BUTTON.SAVE')
      if (this.estimateClientChoiceValidation) {
        return this.$t('ESTIMATE_REPAIRER_CLIENT_CHOICE_VALIDATION.BUTTON.SAVE')
      }
      return this.$t('COMMON.BUTTON.CONTINUE')
    },
    activeFooterPrependIcon() {
      if (this.estimateInOffer || this.estimateClientChoiceValidation) {
        return 'mdi-check'
      }
      return undefined
    },
    activeFooterAppendIcon() {
      if (this.estimateInOffer || this.estimateClientChoiceValidation) {
        return undefined
      }
      return 'mdi-arrow-right'
    },
    headerTitle() {
      if (this.estimateClientChoice || this.estimateClientChoiceValidation) {
        return this.$t('ESTIMATE_REPAIRER.TITLE_CHOICE')
      }
      return this.$t('ESTIMATE_REPAIRER.TITLE')
    }
  },
  unmounted() {
    this.estimateInCreation = true
    this.estimateInOffer = false
    this.estimateInDetail = false
    this.estimateClientChoice = false
    this.estimateClientChoiceValidation = false
  },
  methods: {
    updateFooterState(footerState) {
      this.footerState = footerState
    },
    submitActiveStep() {
      if (!this.activeStepRef) return
      const activeStep = this.$refs[this.activeStepRef]
      if (activeStep?.submit) {
        activeStep.submit()
      }
    },
    resetFooterState() {
      this.footerState = {
        disabled: false,
        loading: false
      }
    },
    resetStepFlags() {
      this.estimateInCreation = false
      this.estimateInOffer = false
      this.estimateInDetail = false
      this.estimateClientChoice = false
      this.estimateClientChoiceValidation = false
      this.resetFooterState()
    },
    handleBack() {
      if (this.estimateInDetail) {
        this.$refs.detailStep?.backToRepairerOffersCreation()
        return
      }
      if (this.estimateInOffer) {
        this.estimateInCreation = true
        this.estimateInOffer = false
        this.estimateInDetail = false
        this.estimateClientChoice = false
        this.estimateClientChoiceValidation = false
        return
      }
      this.handleCancelEstimateRepairer()
    },
    syncStepFromStatus(statusDetail) {
      this.resetStepFlags()
      if (
        statusDetail === RepairFileStatusDetailConstant.REPAIR_FILE_CREATED ||
        statusDetail === RepairFileStatusDetailConstant.COLLECTION_TO_REPAIRER ||
        statusDetail === RepairFileStatusDetailConstant.RECEPTION_AT_REPAIRER ||
        statusDetail === RepairFileStatusDetailConstant.DIAGNOSTIC_MANUFACTURER_RECEIVED
      ) {
        this.estimateInCreation = true
        return
      }
      if (statusDetail === RepairFileStatusDetailConstant.ESTIMATE_CREATED) {
        this.estimateInDetail = true
        return
      }
      if (statusDetail === RepairFileStatusDetailConstant.FORCE_ESTIMATE_CREATED) {
        this.estimateInDetail = true
        return
      }
      if (statusDetail === RepairFileStatusDetailConstant.ESTIMATE_SENT_TO_CUSTOMER) {
        this.estimateClientChoice = true
        return
      }
      if (statusDetail === RepairFileStatusDetailConstant.FORCE_ESTIMATE_SENT_TO_CUSTOMER) {
        this.estimateClientChoice = true
        return
      }
      if (
        statusDetail ===
          RepairFileStatusDetailConstant.ESTIMATE_CUSTOMER_DECISION_SENT_TO_REPAIRER ||
        statusDetail ===
          RepairFileStatusDetailConstant.FORCE_ESTIMATE_CUSTOMER_DECISION_SENT_TO_REPAIRER ||
        statusDetail === RepairFileStatusDetailConstant.IN_REPARATION ||
        statusDetail === RepairFileStatusDetailConstant.SENT_BACK_BY_REPAIRER ||
        statusDetail === RepairFileStatusDetailConstant.RECEIPT_AT_STORE ||
        statusDetail === RepairFileStatusDetailConstant.SENT_AVAILABLE_TO_CUSTOMER ||
        statusDetail === RepairFileStatusDetailConstant.TAKEOVER_BY_CUSTOMER ||
        statusDetail === RepairFileStatusDetailConstant.NETWORK_BILLING ||
        statusDetail === RepairFileStatusDetailConstant.FINISHED ||
        statusDetail === RepairFileStatusDetailConstant.ABORT_BY_CUSTOMER ||
        statusDetail === RepairFileStatusDetailConstant.DELETED
      ) {
        this.$emit('closeNewAction')
        return
      }
      this.estimateInCreation = true
    },
    handleCancelEstimateRepairer() {
      this.estimateInCreation = true
      this.estimateInOffer = false
      this.cancelEstimateRepairer()
    },
    nextStepEstimateInOffer() {
      this.estimateInCreation = false
      this.estimateInOffer = true
      this.$emit('updateCurrentRepairFile')
    },
    handleOpenEstimateRepairerDetail() {
      this.estimateInCreation = false
      this.estimateInOffer = false
      this.estimateInDetail = true
    },
    handleOpenEstimateRepairerClientChoice() {
      this.estimateInDetail = false
      this.estimateClientChoice = true
    },
    handleOpenEstimateRepairerClientChoiceValidation() {
      this.estimateClientChoice = false
      this.estimateClientChoiceValidation = true
    },
    cancelEstimateRepairer() {
      this.$emit('cancelEstimateRepairer')
    },
    handleUpdateCurrentRepairFile() {
      this.$emit('updateCurrentRepairFile')
    },
    handleCloseNewAction() {
      this.$emit('closeNewAction')
    },
    handleBackToRepairerOffersCreation() {
      this.skipNextStatusSync = true
      this.$emit('updateCurrentRepairFile')
      this.estimateInCreation = false
      this.estimateInOffer = true
      this.estimateInDetail = false
      this.estimateClientChoice = false
      this.estimateClientChoiceValidation = false
    }
  }
}
</script>
